import React, { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../config/axios';
import { Users, MapPin, Package, ChevronRight, ChevronLeft, Upload, Check, Play, RotateCcw, FileDown } from 'lucide-react';
import './ExcelImport.css';

const NOT_MAPPED = '__not_mapped__';

const MAPPING_FIELDS = {
  users: [
    { key: 'email', label: 'Email', required: true },
    { key: 'name', label: 'Name', required: true },
    { key: 'password', label: 'Password', required: true },
    { key: 'role', label: 'Role (admin or manager)', required: false },
    { key: 'reportsTo', label: 'Reports To (Admin Name)', required: false }
  ],
  sites: [
    { key: 'siteName', label: 'Site Name', required: true },
    { key: 'siteEmail', label: 'Site Email', required: true },
    { key: 'password', label: 'Password', required: false },
    { key: 'buCode', label: 'BU Code', required: true },
    { key: 'brandName', label: 'Brand Name', required: true },
    { key: 'managerEmail', label: 'Manager Email', required: true },
    { key: 'managerName', label: 'Manager Name', required: true },
    { key: 'country', label: 'Country', required: false },
    { key: 'client', label: 'Client', required: false },
    { key: 'concept', label: 'Concept', required: false },
    { key: 'cluster', label: 'Cluster', required: false },
    { key: 'gln', label: 'GLN', required: false }
  ],
  inventory: [
    { key: 'pivotCategory', label: 'Pivot Category', required: true },
    { key: 'vendorItemCode', label: 'Vendor Item Code', required: true },
    { key: 'vendor', label: 'Vendor', required: true },
    { key: 'itemName', label: 'Item Name', required: true },
    { key: 'itemDescription', label: 'Item Description', required: true },
    { key: 'packSize', label: 'Pack Size', required: true },
    { key: 'unit1', label: 'Unit 1', required: true },
    { key: 'value1', label: 'Value 1', required: true },
    { key: 'unit2', label: 'Unit 2', required: false },
    { key: 'value2', label: 'Value 2', required: false },
    { key: 'location', label: 'Location', required: false },
    { key: 'priority', label: 'Priority', required: false }
  ]
};



const POLL_INTERVAL_MS = 1500;
const IMPORT_TYPES = [
  { value: 'users', label: 'Users', icon: Users },
  { value: 'sites', label: 'Sites', icon: MapPin },
  { value: 'inventory', label: 'Inventory', icon: Package }
];
const STEP_LABELS = ['Select Type', 'Upload File', 'Map Columns', 'Run'];

function getDefaultOptions(type) {
  return {
    importMode: 'createOnly',
    siteMatchKey: type === 'sites' ? 'buCode' : 'name',
    upsertKey: 'vendorItemCode',
    blankClears: false,
    acknowledgeBlankClears: false,
    allowlistFields: []
  };
}

function getDefaultMapping(type) {
  const fields = MAPPING_FIELDS[type] || [];
  const out = {};
  fields.forEach((f) => { out[f.key] = ''; });
  return out;
}

function headerToCamelCase(header) {
  if (!header || typeof header !== 'string') return '';
  return header.trim().replace(/[\s_-]+(\w)/g, (_, c) => c.toUpperCase()).replace(/^./, (c) => c.toLowerCase());
}

export default function AdvancedImportWizard({ defaultBrandId, brands = [], onSuccess, onJobCreated, onTypeChange }) {
  const [step, setStep] = useState(1);
  const [importType, setImportType] = useState('inventory');
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [headersLoading, setHeadersLoading] = useState(false);
  const [headersError, setHeadersError] = useState('');
  const [mapping, setMapping] = useState(() => getDefaultMapping('inventory'));
  const [options, setOptions] = useState(() => getDefaultOptions('inventory'));
  const [runJobId, setRunJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState('');
  const [progressPct, setProgressPct] = useState(0);
  const [runSummary, setRunSummary] = useState(null);
  const [runErrorMessage, setRunErrorMessage] = useState('');
  const [runLoading, setRunLoading] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [profileNameToSave, setProfileNameToSave] = useState('');
  const [profileSaveLoading, setProfileSaveLoading] = useState(false);
  const [profileLoadLoading, setProfileLoadLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const type = importType;
  const baseFields = type === 'users' ? MAPPING_FIELDS.users : (MAPPING_FIELDS[type] || []);
  const hasRole = baseFields.some((f) => f.key === 'role');
  const fields = type === 'users' && !hasRole
    ? [...baseFields, { key: 'role', label: 'Role (admin or manager)', required: false }]
    : baseFields;
  const isInventory = type === 'inventory';
  const [selectedBrandId, setSelectedBrandId] = useState(() =>
    defaultBrandId != null ? String(defaultBrandId) : (brands && brands[0] ? String(brands[0].id != null ? brands[0].id : brands[0].value) : '')
  );
  const brandId = selectedBrandId || (defaultBrandId != null ? String(defaultBrandId) : '');

  const fetchHeaders = useCallback(async () => {
    if (!file) return;
    setHeadersLoading(true);
    setHeadersError('');
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axiosInstance.post(`/api/tenant/import/${type}/headers`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setHeaders(res.data.headers || []);
    } catch (err) {
      setHeadersError(err.response?.data?.error || err.message || 'Failed to get headers');
      setHeaders([]);
    } finally {
      setHeadersLoading(false);
    }
  }, [file, type]);

  const fetchProfiles = useCallback(async () => {
    setProfileLoadLoading(true);
    try {
      const res = await axiosInstance.get(`/api/tenant/import/profiles?type=${type}`);
      setProfiles(res.data.profiles || []);
    } catch (_) {
      setProfiles([]);
    } finally {
      setProfileLoadLoading(false);
    }
  }, [type]);

  const isMappingStep = step === 3;
  useEffect(() => {
    if (file && isMappingStep) fetchHeaders();
  }, [file, isMappingStep, fetchHeaders]);

  const isProfileStepOrLater = step >= 3;
  useEffect(() => {
    if (isProfileStepOrLater) fetchProfiles();
  }, [isProfileStepOrLater, type, fetchProfiles]);

  useEffect(() => {
    if (isInventory && defaultBrandId != null) setSelectedBrandId(String(defaultBrandId));
  }, [isInventory, defaultBrandId]);

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const processFile = (f) => {
    if (!f) return;
    const ext = (f.name || '').toLowerCase().slice(-5);
    if (!['.xlsx', '.xls', '.csv'].some((e) => ext.endsWith(e))) {
      setHeadersError('File must be .xlsx, .xls, or .csv');
      setFile(null);
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setHeadersError('File exceeds the 10 MB limit.');
      setFile(null);
      return;
    }
    setFile(f);
    setHeadersError('');
    setHeaders([]);
    setMapping(getDefaultMapping(type));
    setRunJobId(null);
    setRunSummary(null);
    setRunErrorMessage('');
  };

  const handleFileChange = (e) => processFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    processFile(f);
  };

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragging(false); };

  const setMappingField = (systemField, headerName) => {
    setMapping((prev) => ({
      ...prev,
      [systemField]: headerName === NOT_MAPPED ? '' : headerName
    }));
  };

  const loadProfile = async () => {
    if (!selectedProfileId) return;
    setProfileLoadLoading(true);
    try {
      const res = await axiosInstance.get(`/api/tenant/import/profiles/${selectedProfileId}`);
      const profile = res.data;
      if (profile) {
        if (profile.mapping_json != null) {
          const m = typeof profile.mapping_json === 'string' ? JSON.parse(profile.mapping_json) : profile.mapping_json;
          setMapping((prev) => ({ ...getDefaultMapping(type), ...m }));
        }
        if (profile.options_json != null) {
          const o = typeof profile.options_json === 'string' ? JSON.parse(profile.options_json) : profile.options_json;
          setOptions((prev) => ({ ...getDefaultOptions(type), ...o, allowlistFields: Array.isArray(o && o.allowlistFields) ? o.allowlistFields : (prev.allowlistFields || []) }));
        }
      }
    } catch (_) {}
    setProfileLoadLoading(false);
  };

  const saveProfile = async () => {
    const name = (profileNameToSave || '').trim();
    if (!name) return;
    setProfileSaveLoading(true);
    try {
      await axiosInstance.post('/api/tenant/import/profiles', { type, name, mapping_json: mapping, options_json: options });
      setProfileNameToSave('');
      fetchProfiles();
    } catch (err) {
      setHeadersError(err.response?.data?.error || 'Failed to save profile');
    } finally {
      setProfileSaveLoading(false);
    }
  };

  const runJob = async () => {
    if (!file) return;
    if (isInventory && !brandId) return;
    setRunLoading(true);
    setRunErrorMessage('');
    setRunSummary(null);
    const form = new FormData();
    form.append('type', type);
    form.append('file', file);
    form.append('mapping_json', JSON.stringify(mapping));
    form.append('options_json', JSON.stringify(options));
    if (selectedProfileId) form.append('profile_id', selectedProfileId);
    if (isInventory && brandId) form.append('brandId', brandId);
    try {
      const createRes = await axiosInstance.post('/api/tenant/import/jobs', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      const jobId = createRes.data.jobId;
      if (!jobId) throw new Error('No job ID returned');
      setRunJobId(jobId);
      if (onJobCreated) onJobCreated();
      await axiosInstance.post(`/api/tenant/import/jobs/${jobId}/run`);
      const poll = async () => {
        const jobRes = await axiosInstance.get(`/api/tenant/import/jobs/${jobId}`);
        const j = jobRes.data;
        setJobStatus(j.status || '');
        setProgressPct(j.progress_pct != null ? j.progress_pct : 0);
        if (j.status === 'completed') { setRunSummary(j.summary || {}); if (onSuccess) onSuccess(); return; }
        if (j.status === 'failed') { setRunErrorMessage(j.error_message || 'Job failed'); setRunLoading(false); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      };
      poll();
    } catch (err) {
      setRunErrorMessage(err.response?.data?.error || err.message || 'Failed to start job');
    }
    setRunLoading(false);
  };

  useEffect(() => {
    if (!runJobId || !runLoading) return;
    const t = setInterval(async () => {
      try {
        const res = await axiosInstance.get(`/api/tenant/import/jobs/${runJobId}`);
        const j = res.data;
        setJobStatus(j.status || '');
        setProgressPct(j.progress_pct != null ? j.progress_pct : 0);
        if (j.status === 'completed') { setRunSummary(j.summary || {}); setRunLoading(false); if (onSuccess) onSuccess(); }
        else if (j.status === 'failed') { setRunErrorMessage(j.error_message || 'Job failed'); setRunLoading(false); }
      } catch (_) {}
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [runJobId, runLoading, onSuccess]);

  const hasAtLeastOneValue = !isInventory || (mapping.value1 != null && String(mapping.value1).trim() !== '') || (mapping.value2 != null && String(mapping.value2).trim() !== '');
  const blockRun = (isInventory && !brandId) || (isInventory && !hasAtLeastOneValue);
  const blockRunMessage = (isInventory && !brandId) ? 'Select a brand for inventory import.' : (isInventory && !hasAtLeastOneValue) ? 'Map at least one value: Value 1 or Value 2.' : '';



  const handleSelectType = (t) => {
    setImportType(t);
    setMapping(getDefaultMapping(t));
    setOptions(getDefaultOptions(t));
    setFile(null);
    setHeaders([]);
    setRunJobId(null);
    setRunSummary(null);
    setRunErrorMessage('');
    if (onTypeChange) onTypeChange(t);
  };

  const downloadTemplate = async () => {
    try {
      const res = await axiosInstance.get(`/api/tenant/import/template/${importType}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `SimplyInventory_${importType.charAt(0).toUpperCase() + importType.slice(1)}_Import_Template.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setHeadersError(err.response?.data?.error || err.message || 'Failed to download template');
    }
  };

  return (
    <div className="card iw-card">
      <h3 className="iw-title">Import Data</h3>

      <div className="iw-steps">
        {STEP_LABELS.map((label, idx) => {
          const num = idx + 1;
          const isActive = step === num;
          const isDone = step > num;
          return (
            <React.Fragment key={num}>
              {idx > 0 && <div className={'iw-step-line' + (isDone ? ' done' : '')} />}
              <div className={'iw-step' + (isActive ? ' active' : '') + (isDone ? ' done' : '')}>
                <div className="iw-step-circle">{isDone ? <Check size={14} strokeWidth={3} /> : num}</div>
                <span className="iw-step-label">{label}</span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && (
        <div className="iw-step-content">
          <p className="iw-step-desc">Choose the type of data you want to import.</p>
          <div className="iw-type-cards">
            {IMPORT_TYPES.map(({ value, label, icon: Icon }) => (
              <button key={value} type="button" className={'iw-type-card' + (importType === value ? ' active' : '')} onClick={() => handleSelectType(value)}>
                <Icon size={22} />
                <span>{label}</span>
              </button>
            ))}
          </div>
          <div className="iw-nav">
            <button type="button" className="btn btn-secondary iw-btn-download-template" onClick={downloadTemplate} title={`Download ${importType} import template`}>
              <FileDown size={16} /> Download template
            </button>
            <button type="button" className="btn iw-btn-next" onClick={() => setStep(2)}>
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="iw-step-content">
          <p className="iw-step-desc">Upload your <strong>{importType}</strong> file. Supported formats: .xlsx, .csv</p>
          <input type="file" ref={fileInputRef} className="iw-file-hidden" accept=".xlsx,.xls,.csv" onChange={handleFileChange} />
          {headersError && <div className="iw-error"><p>{headersError}</p></div>}
          {!file ? (
            <div className={'iw-dropzone' + (dragging ? ' dragging' : '')} onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onClick={() => fileInputRef.current?.click()}>
              <Upload size={28} className="iw-dropzone-icon" />
              <p className="iw-dropzone-text">Drag & drop your file here, or <span className="iw-dropzone-browse">browse</span></p>
              <p className="iw-dropzone-hint">.xlsx or .csv, max 10MB</p>
            </div>
          ) : (
            <div className="iw-file-info">
              <strong>{file.name}</strong> ({(file.size / 1024).toFixed(1)} KB)
              <button type="button" className="iw-file-remove" onClick={() => { setFile(null); setHeaders([]); }}>Change file</button>
            </div>
          )}
          <div className="iw-nav">
            <button type="button" className="btn iw-btn-back" onClick={() => setStep(1)}><ChevronLeft size={14} /> Back</button>
            <button type="button" className="btn iw-btn-next" disabled={!file} onClick={() => setStep(3)}>
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="iw-step-content">
          {headersLoading && <p>Loading headers...</p>}
          {headersError && <div className="iw-error"><p>{headersError}</p></div>}
          {isInventory && !hasAtLeastOneValue && headers.length > 0 && (
            <div className="iw-error"><p>Map at least one value: Value 1 or Value 2.</p></div>
          )}
          {!headersLoading && headers.length > 0 && (
            <>
              <div className="form-group">
                <label>Load profile (optional)</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select className="form-control" style={{ maxWidth: 280 }} value={selectedProfileId} onChange={(e) => setSelectedProfileId(e.target.value)}>
                    <option value="">-- None --</option>
                    {(profiles || []).map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                  </select>
                  <button type="button" className="btn btn-secondary" disabled={!selectedProfileId || profileLoadLoading} onClick={loadProfile}>Load</button>
                </div>
              </div>
              <div className="form-group">
                <label>Map columns</label>
                {fields.map((f) => (
                  <div key={f.key} className="form-group" style={{ marginBottom: '8px' }}>
                    <label className="iw-map-label">{f.label}{f.required ? ' *' : ''}</label>
                    <select className="form-control" value={mapping[f.key] === '' ? NOT_MAPPED : mapping[f.key]} onChange={(e) => setMappingField(f.key, e.target.value)}>
                      <option value={NOT_MAPPED}>Not mapped</option>
                      {headers.map((h) => (<option key={h} value={h}>{h}</option>))}
                    </select>
                  </div>
                ))}
                {type === 'sites' && (() => {
                  const baseKeys = new Set(fields.map((f) => f.key));
                  const mappedHeaders = new Set(Object.values(mapping).filter((v) => v && v !== NOT_MAPPED));
                  const unmappedHeaders = headers.filter((h) => !mappedHeaders.has(h));
                  const addedKeys = Object.keys(mapping).filter((k) => !baseKeys.has(k) && mapping[k]);
                  return (
                    <>
                      {addedKeys.length > 0 && (
                        <div style={{ marginTop: '8px', marginBottom: '8px' }}>
                          <label className="iw-map-label">Additional fields</label>
                          {addedKeys.map((key) => (
                            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                              <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>{key}</span>
                              <span style={{ fontSize: '13px' }}>&rarr; {mapping[key]}</span>
                              <button type="button" className="btn btn-sm btn-danger" style={{ padding: '2px 6px' }} onClick={() => setMapping((prev) => { const next = { ...prev }; delete next[key]; return next; })}>x</button>
                            </div>
                          ))}
                        </div>
                      )}
                      {unmappedHeaders.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                          <button type="button" className="btn btn-sm btn-secondary" onClick={() => { const h = unmappedHeaders[0]; const key = headerToCamelCase(h); if (key) setMapping((prev) => ({ ...prev, [key]: h })); }}>Add field</button>
                          {unmappedHeaders.length > 1 && (
                            <select className="form-control" style={{ maxWidth: 200 }} value="" onChange={(e) => { const h = e.target.value; if (!h) return; const key = headerToCamelCase(h); if (key) setMapping((prev) => ({ ...prev, [key]: h })); e.target.value = ''; }}>
                              <option value="">-- Pick column to add --</option>
                              {unmappedHeaders.map((h) => (<option key={h} value={h}>{h}</option>))}
                            </select>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              {type === 'users' && <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '8px' }}>Role can be mapped from a spreadsheet column. If left unmapped, default to manager.</p>}
              <div className="form-group">
                <label>Import mode</label>
                <select
                  className="form-control"
                  style={{ maxWidth: 360 }}
                  value={options.importMode || 'createOnly'}
                  onChange={(e) => setOptions((prev) => ({ ...prev, importMode: e.target.value }))}
                >
                  <option value="createOnly">Create new only (skip existing records)</option>
                  <option value="createAndUpdate">Create new and update existing</option>
                </select>
                <small style={{ color: 'var(--color-text-muted)' }}>
                  In update mode, blank cells leave existing values unchanged. Existing records are never deleted.
                </small>
              </div>
              {type === 'sites' && (
                <div className="form-group">
                  <label>Match existing sites by</label>
                  <select
                    className="form-control"
                    style={{ maxWidth: 280 }}
                    value={options.siteMatchKey || 'buCode'}
                    onChange={(e) => setOptions((prev) => ({ ...prev, siteMatchKey: e.target.value }))}
                  >
                    <option value="buCode">BU Code (recommended)</option>
                    <option value="name">Site Name</option>
                  </select>
                </div>
              )}
              {isInventory && (
                <div className="form-group">
                  <label>Match existing inventory by</label>
                  <select
                    className="form-control"
                    style={{ maxWidth: 280 }}
                    value={options.upsertKey || 'vendorItemCode'}
                    onChange={(e) => setOptions((prev) => ({ ...prev, upsertKey: e.target.value }))}
                  >
                    <option value="vendorItemCode">Vendor Item Code (recommended)</option>
                    <option value="itemName">Item Name</option>
                  </select>
                </div>
              )}
              {isInventory && (
                <div className="form-group">
                  <label>Brand *</label>
                  <select className="form-control" value={brandId} onChange={(e) => setSelectedBrandId(e.target.value)}>
                    <option value="">Select brand</option>
                    {(brands || []).map((b) => (<option key={b.id || b.value} value={String(b.id != null ? b.id : b.value)}>{b.name || b.label || b.id}</option>))}
                  </select>
                  <small style={{ color: 'var(--color-text-muted)' }}>Brand is required for inventory import.</small>
                </div>
              )}
              <div className="form-group">
                <label>Save as profile</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input type="text" className="form-control" style={{ maxWidth: 240 }} placeholder="Profile name" value={profileNameToSave} onChange={(e) => setProfileNameToSave(e.target.value)} />
                  <button type="button" className="btn btn-secondary" disabled={!profileNameToSave.trim() || profileSaveLoading} onClick={saveProfile}>Save</button>
                </div>
              </div>
            </>
          )}
          <div className="iw-nav">
            <button type="button" className="btn iw-btn-back" onClick={() => setStep(2)}><ChevronLeft size={14} /> Back</button>
            <button type="button" className="btn iw-btn-next" onClick={() => setStep(4)}>
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="iw-step-content">
          <div className="iw-run-tiles">
            <div className={'card iw-run-tile' + (runSummary ? ' iw-tile-success' : '')}>
              <span className="iw-run-tile-label">CREATED</span>
              <span className="iw-run-tile-value">{runSummary?.created ?? 0}</span>
            </div>
            <div className={'card iw-run-tile' + (runSummary && (runSummary.updated ?? 0) > 0 ? ' iw-tile-success' : '')}>
              <span className="iw-run-tile-label">UPDATED</span>
              <span className="iw-run-tile-value">{runSummary?.updated ?? 0}</span>
            </div>
            <div className={'card iw-run-tile' + (runSummary && (runSummary.skips ?? 0) > 0 ? ' iw-tile-warning' : '')}>
              <span className="iw-run-tile-label">SKIPPED</span>
              <span className="iw-run-tile-value">{runSummary?.skips ?? 0}</span>
            </div>
            <div className={'card iw-run-tile' + (runSummary && (runSummary.errors ?? 0) > 0 ? ' iw-tile-danger' : '')}>
              <span className="iw-run-tile-label">ERRORS</span>
              <span className="iw-run-tile-value">{runSummary?.errors ?? 0}</span>
            </div>
          </div>

          {blockRun && <div className="iw-error"><p>{blockRunMessage}</p></div>}
          {runErrorMessage && <div className="iw-error"><p>{runErrorMessage}</p></div>}

          {runLoading && (
            <div className="iw-progress">
              <div className="iw-progress-header">
                <span className="iw-progress-status">{jobStatus === 'running' ? 'Importing...' : (jobStatus || 'Starting...')}</span>
                <span className="iw-progress-pct">{progressPct}%</span>
              </div>
              <div className="iw-progress-track">
                <div className="iw-progress-bar" style={{ width: progressPct + '%' }} />
              </div>
            </div>
          )}

          {runJobId && !runLoading && !runSummary && (
            <div className="iw-info">Job created. Check Job History below for status.</div>
          )}

          {!runSummary ? (
            <button type="button" className="btn iw-btn-run" disabled={blockRun || runLoading} onClick={runJob}>
              <Play size={15} /> {runLoading ? 'Running...' : 'Run Import'}
            </button>
          ) : (
            <button type="button" className="btn iw-btn-startover" onClick={() => { setStep(1); setFile(null); setRunJobId(null); setRunSummary(null); }}>
              <RotateCcw size={14} /> Start Over
            </button>
          )}

          <div className="iw-nav">
            <button type="button" className="btn iw-btn-back" onClick={() => setStep(3)}><ChevronLeft size={14} /> Back</button>
            <div />
          </div>
        </div>
      )}
    </div>
  );
}